import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { DefaultInput, DefaultProduct, SimpleProductsTier } from "@PayPlans/helpers/DefaultSections/Commons";
import { getProductsErrors } from "@/Common/helpers/Matrices/products";

// Hooks
import { useValidations } from "@/Common/hooks/Validations";
import { useCallbackState } from "@/Common/hooks/CallbackState";

// Components
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";
import TierExpandableHeader from "@/Layouts/components/Tiers/TierExpandableHeader";
import TierProducts from "./TierProducts";
import TierRows from "@/Layouts/components/Tiers/TierRows";
import MiniDealCommissions from "@Motivators/components/SpecialUnits/SpecialUnitsModelTiered/MiniDealCommissions";

// MUI
import { Grid, Stack } from "@mui/material";

// Constants
const CLOSE_TIME = 280;
const OVERLOAD_LABEL = "Product commission over 100%";

const getMappedProducts = (products = []) => products.map((item) => DefaultProduct(0.0, item.input, item.product));

const useAvailableProducts = (products = [], selectedProducts = []) => {
	return products
		.filter((product) => !selectedProducts.some((item) => item.label === product.label))
		.map((product) => ({ label: product.label }));
};

const getTotalColumns = (tiers = []) => {
	const frontProductsLength = tiers?.[0]?.frontProducts?.length || 0;
	const backProductsLength = tiers?.[0]?.backProducts?.length || 0;
	const frontArea = frontProductsLength >= 1 ? 2 : 1;
	const backArea = backProductsLength >= 1 ? 2 : 1;
	return frontArea + backArea;
};

const getAreaWidth = (productLength = 0, type = "all") => {
	const AREA_SMALL = 3;
	const AREA_BIG = 5;
	const ITEM_SMALL = 6;
	const ITEM_BIG = 12;

	const SMALL_AREA = type === "item" ? ITEM_SMALL : AREA_SMALL;
	const BIG_AREA = type === "item" ? ITEM_BIG : AREA_BIG;

	if (type === "item") {
		return productLength >= 1 ? ITEM_SMALL : ITEM_BIG;
	}

	return productLength >= 1 ? BIG_AREA : SMALL_AREA;
};

const getTiersReduction = (tiers = []) => {
	const MIN_REDUCTION = 3;
	const UP_REDUCED = -25;
	const LOW_REDUCED = -13;

	if (getTotalColumns(tiers) <= 2) {
		return UP_REDUCED;
	}
	return getTotalColumns(tiers) > MIN_REDUCTION ? 0 : LOW_REDUCED;
};

const validateError = (errors, key) => errors.get(key) || [];

const isDeleteButtonDisabled = (innerTiers, readOnly) => Boolean(innerTiers?.length <= 1) || readOnly;

const getTotalRows = (innerTiers) => innerTiers?.length || 1;

const isFrontAddDisabled = (frontAvailableProducts, readOnly) => !frontAvailableProducts?.length || readOnly;

const isFrontRemoveDisabled = (innerTiers, readOnly) => !innerTiers?.[0]?.frontProducts?.length || readOnly;

const isBackAddDisabled = (backAvailableProducts, readOnly) => !backAvailableProducts?.length || readOnly;

const isBackRemoveDisabled = (innerTiers, readOnly) => !innerTiers?.[0]?.backProducts?.length || readOnly;

const miniDealModel = (type, data, miniDeal) => {
	const dealModel = { ...miniDeal, [type]: data };
	if (type === "cap") {
		dealModel["capEnabled"] = data?.value !== 0;
	}
	return dealModel;
};

export default function TieredProduct(props) {
	const {
		tiers,
		miniDeal,
		frontProducts = [],
		backProducts = [],
		loadingProducts = false,
		additionalErrors = [],
		readOnly = false,
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// Hooks
	const { errors, isValid, getErrorsMessages, validate } = useValidations();
	const unitsErrors = validateError(errors, "units");
	const commissionErrors = validateError(errors, "commissions");
	const backGrossErrors = validateError(errors, "backGross");
	const frontCommissionPercentErrors = validateError(errors, "frontCommissions");
	const backCommissionPercentErrors = validateError(errors, "backCommissions");
	const frontProductsErrors = validateError(errors, "frontProducts");
	const backProductsErrors = validateError(errors, "backProducts");

	const localErrorsMessages = getErrorsMessages();

	const errorsMessages = useMemo(
		() => [...new Set([...localErrorsMessages, ...additionalErrors])],
		[localErrorsMessages, additionalErrors]
	);

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onTiersUpdate = (updatedTiers, type) => {
		onActionValidation?.(isValid);
		if (isValid && wasUpdated.current) {
			onActionUpdate(updatedTiers, type);
		}
	};

	// States
	const [innerTiers, setInnerTiers] = useCallbackState([], (updatedTiers) => onTiersUpdate(updatedTiers, "tiers"));
	const [innerDeal, setInnerDeal] = useState(null);
	const [frontSelectedProducts, setFrontSelectedProducts] = useState([]);
	const [backSelectedProducts, setBackSelectedProducts] = useState([]);

	const frontAvailableProducts = useAvailableProducts(frontProducts, frontSelectedProducts);
	const backAvailableProducts = useAvailableProducts(backProducts, backSelectedProducts);

	const [areaWidth, setAreaWidth] = useState({
		front: 3,
		back: 3,
		fItem: 12,
		bItem: 12,
	});
	const [inputRelated, setInputRelated] = useState({
		area: "",
		column: "",
	});
	const [selectRelated, setSelectRelated] = useState({
		area: "",
		column: "",
	});
	const [blinkSelect, setBlinkSelect] = useState(false);

	useMemo(() => {
		const tier = innerTiers?.[0] || null;

		// Map products
		const defaultFrontProducts = tier?.frontProducts?.map((item) => ({ label: item.product })) || [];
		const defaultBackProducts = tier?.backProducts?.map((item) => ({ label: item.product })) || [];

		// Change areas
		const frontProductsLength = tier?.frontProducts?.length || 0;
		const backProductsLength = tier?.backProducts?.length || 0;

		// Execute validations
		if (innerTiers.length) {
			const frontDistinct = frontProductsLength ? {} : { distinct: 0 };

			validate([
				{
					definition: {
						useKey: "units",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Unit: 0.5 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers,
				},
				{
					definition: {
						useKey: "commissions",
						operators: {
							conditions: [
								{
									type: "SELF",
									on: "value",
									...frontDistinct,
								},
								{
									type: "STEP",
									on: "value",
									compare: "<=", // If current (upper) < next is good
									deltas: { Dollar: 0, Percent: 0 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["frontGross"],
						message: "Invalid front commission",
						validationType: "SIMPLE",
					},
					section: innerTiers,
				},
				{
					definition: {
						useKey: "backGross",
						operators: {
							conditions: [
								{
									type: "SELF",
									on: "value",
								},
								{
									type: "STEP",
									on: "value",
									compare: "<=", // If current (upper) <= next is good
									deltas: { Dollar: 0, Percent: 0 }, // Add delta on upper value
								},
							],
						},
						errorsMap: ["backGross"],
						message: "Invalid back commissions",
						validationType: "SIMPLE",
					},
					section: innerTiers,
				},
				{
					definition: {
						useKey: "frontCommissions",
						operators: {
							conditions: [
								{
									type: "ROW",
									on: "value",
									when: { key: "input", value: "Percent" },
									operation: "SUM",
									compare: "<=",
									limit: 100,
								},
							],
						},
						errorsMap: ["frontGross", "frontProducts"],
						message: OVERLOAD_LABEL,
						validationType: "OPERATION",
					},
					section: innerTiers,
				},
				{
					definition: {
						useKey: "backCommissions",
						operators: {
							conditions: [
								{
									type: "ROW",
									on: "value",
									when: { key: "input", value: "Percent" },
									operation: "SUM",
									compare: "<=",
									limit: 100,
								},
							],
						},
						errorsMap: ["backGross", "backProducts"],
						message: OVERLOAD_LABEL,
						validationType: "OPERATION",
					},
					section: innerTiers,
				},
				{
					definition: {
						useKey: "frontProducts",
						validationType: "CUSTOM",
						errorsMap: ["tiers"],
						message: "Invalid front products commissions",
						operators: {
							customFn: (params = null) => {
								const localErrors = getProductsErrors(params);
								return [...localErrors];
							},
							params: {
								identifier: "frontProducts",
								tiers: innerTiers,
								products: defaultFrontProducts,
							},
						},
					},
					section: innerTiers,
				},
				{
					definition: {
						useKey: "backProducts",
						validationType: "CUSTOM",
						errorsMap: ["tiers"],
						message: "Invalid back products commissions",
						operators: {
							customFn: (params = null) => {
								const localErrors = getProductsErrors(params);
								return [...localErrors];
							},
							params: {
								identifier: "backProducts",
								tiers: innerTiers,
								products: defaultBackProducts,
							},
						},
					},
					section: innerTiers,
				},
			]);
		}

		setFrontSelectedProducts(defaultFrontProducts);
		setBackSelectedProducts(defaultBackProducts);
		setAreaWidth({
			front: getAreaWidth(frontProductsLength),
			back: getAreaWidth(backProductsLength),
			fItem: getAreaWidth(frontProductsLength, "item"),
			bItem: getAreaWidth(backProductsLength, "item"),
		});
	}, [innerTiers, validate]);

	const saveTiers = (updatedTiers) => {
		wasUpdated.current = true;
		setInnerTiers(updatedTiers);
	};

	const handleAddTier = () => {
		const actualTiers = [...innerTiers];

		const lastIndex = actualTiers?.length ? actualTiers.length - 1 : 0;
		const frontInput = actualTiers?.[lastIndex]?.frontGross?.input || "Percent";
		const backInput = actualTiers?.[lastIndex]?.backGross?.input || "Percent";

		const from = DefaultInput(0, "Unit");
		const to = DefaultInput(100000, "Unit");
		const frontGross = DefaultInput(0, frontInput);
		const backGross = DefaultInput(0, backInput);

		const currentFrontProducts = getMappedProducts(actualTiers?.[lastIndex]?.frontProducts) || [];
		const currentBackProducts = getMappedProducts(actualTiers?.[lastIndex]?.backProducts) || [];

		if (actualTiers?.length) {
			actualTiers[lastIndex].to.value = 0;
		}

		const newTier = SimpleProductsTier(from, to, {
			frontGross,
			backGross,
			frontProducts: currentFrontProducts,
			backProducts: currentBackProducts,
		});

		actualTiers.push(newTier);
		saveTiers(actualTiers);
	};

	const handleRemoveTier = () => {
		const actualTiers = [...innerTiers];
		if (actualTiers.length > 1) {
			actualTiers.splice(-1);
		}

		const lastIndex = actualTiers?.length ? actualTiers.length - 1 : 0;
		actualTiers[lastIndex].to.value = 100000;
		saveTiers(actualTiers);
	};

	const handleAddProduct = (area) => {
		const actualTiers = [...innerTiers];
		const productsAvailable = area === "frontProducts" ? [...frontAvailableProducts] : [...backAvailableProducts];
		const productLabel = productsAvailable[0]?.label;

		// Add first available product for all correspondent areas
		actualTiers?.forEach((tier) => {
			tier[area].push(DefaultProduct(0.0, "Percent", productLabel));
		});

		saveTiers(actualTiers);
	};

	const handleRemoveProduct = (area) => {
		const actualTiers = [...innerTiers];
		if (actualTiers?.[0]?.[area]?.length) {
			// Remove last product for all correspondent areas
			actualTiers.forEach((tier) => {
				tier[area].splice(-1);
			});
		}
		saveTiers(actualTiers);
	};

	const handleOpenSelect = (area, column) => {
		setBlinkSelect(false);
		setSelectRelated({ area, column });
	};

	const handleCloseSelect = () => {
		setTimeout(() => {
			if (blinkSelect) {
				setBlinkSelect(false);
			}
			setSelectRelated({ area: "", column: "" });
		}, CLOSE_TIME);
	};

	const handleSelect = (area, index, label) => {
		const actualTiers = [...innerTiers];
		actualTiers.forEach((tier) => {
			tier[area][index] = DefaultProduct(0.0, "Percent", label);
		});
		setBlinkSelect(true);
		saveTiers(actualTiers);
	};

	const handleUpdateUnits = (area, index, value, type) => {
		const actualTiers = [...innerTiers];
		const newValue = parseFloat(value) || 0;
		actualTiers[index][area] = DefaultInput(newValue, type);
		if (actualTiers?.[index + 1] && area === "to") {
			actualTiers[index + 1]["from"]["value"] = Number(newValue) + 0.5;
		}
		saveTiers(actualTiers);
	};

	const handleClickInput = (area, column) => {
		setInputRelated({ area, column });
	};

	const handleFocusOutInput = () => {
		setInputRelated({ area: "", column: "" });
	};

	const handleTypeChangeInput = (area, _row, column, type) => {
		const actualTiers = [...innerTiers];
		if (typeof type !== "undefined") {
			switch (area) {
				case "frontGross":
				case "backGross":
					actualTiers.forEach((tier) => {
						tier[area]["input"] = type;
						tier[area]["value"] = 0;
					});
					break;
				default:
					actualTiers.forEach((tier) => {
						tier[area][column]["input"] = type;
						tier[area][column]["value"] = 0;
					});
					break;
			}
			saveTiers(actualTiers);
		}
	};

	const handleUpdateInput = (area, row, column, value = 0) => {
		const actualTiers = [...innerTiers];
		const newValue = value ? parseFloat(value) : 0;

		switch (area) {
			case "frontGross":
			case "backGross":
				actualTiers[row][area]["value"] = newValue;
				break;
			default:
				actualTiers[row][area][column]["value"] = newValue;
				break;
		}

		saveTiers(actualTiers);
	};

	const handleUpdateDeal = (type, data) => {
		const updatedDeal = miniDealModel(type, data, innerDeal);
		onActionUpdate(updatedDeal, "miniDeal");
	};

	useEffect(() => {
		setInnerTiers(clone(tiers));

		if (miniDeal) {
			setInnerDeal(clone(miniDeal));
		}

		return () => {
			setInnerTiers([]);
			setInnerDeal(null);
			setFrontSelectedProducts([]);
			setBackSelectedProducts([]);
			setInputRelated({
				area: "",
				column: "",
			});
			setSelectRelated({
				area: "",
				column: "",
			});
			setBlinkSelect(false);
			wasUpdated.current = false;
		};
	}, [tiers, miniDeal, setInnerTiers]);

	return (
		<>
			<TierRows
				deleteButtonText={"Remove Tier"}
				addButtonText={"Add Tier"}
				deleteButtonDisabled={isDeleteButtonDisabled(innerTiers, readOnly)}
				addButtonDisabled={readOnly}
				spaces={{
					mainSection: 155.82,
					columns: 215.35,
					totalColumns: getTotalColumns(innerTiers),
					totalRows: getTotalRows(innerTiers),
					separation: 10,
				}}
				containerProps={{
					position: "relative",
					mt: 0.5,
				}}
				onClickDelete={handleRemoveTier}
				onClickAdd={handleAddTier}
			>
				<TierExpandableHeader
					headers={[
						{ title: "Units" },
						{
							title: "Front End",
							expandible: true,
							showDivider: true,
							loading: loadingProducts,
							actions: {
								disabledAdd: isFrontAddDisabled(frontAvailableProducts, readOnly),
								disabledRemove: isFrontRemoveDisabled(innerTiers, readOnly),
								onActionAdd: () => handleAddProduct("frontProducts"),
								onActionRemove: () => handleRemoveProduct("frontProducts"),
							},
							itemProps: { sm: 12, md: areaWidth.front },
							style: { maxWidth: areaWidth.front === 3 ? "22%" : null },
						},
						{
							title: "Back End",
							expandible: true,
							showDivider: true,
							loading: loadingProducts,
							actions: {
								disabledAdd: isBackAddDisabled(backAvailableProducts, readOnly),
								disabledRemove: isBackRemoveDisabled(innerTiers, readOnly),
								onActionAdd: () => handleAddProduct("backProducts"),
								onActionRemove: () => handleRemoveProduct("backProducts"),
							},
							itemProps: { sm: 12, md: areaWidth.back },
							style: { maxWidth: areaWidth.back === 3 ? "22%" : null },
						},
					]}
				/>

				<TierProducts
					rows={innerTiers}
					rowItemSpaces={{
						mainSection: 155.82,
						columns: 215.35,
						totalColumns: getTotalColumns(innerTiers),
						separation: 10,
						reduction: getTiersReduction(innerTiers),
					}}
					units={{
						firstInputIndex: "from",
						secondInputIndex: "to",
						callbacks: { onActionUpdate: handleUpdateUnits },
					}}
					productWidthAreas={{
						front: {
							total: areaWidth.front,
							items: areaWidth.fItem,
						},
						back: {
							total: areaWidth.back,
							items: areaWidth.bItem,
						},
					}}
					readOnly={readOnly}
					inputRelated={inputRelated}
					selectRelated={selectRelated}
					errorsUnits={unitsErrors}
					errorCommission={commissionErrors}
					backGrossErrors={backGrossErrors}
					frontProductsErrors={frontProductsErrors}
					backProductsErrors={backProductsErrors}
					errorPercentageFront={frontCommissionPercentErrors}
					errorPercentageBack={backCommissionPercentErrors}
					blinkSelect={blinkSelect}
					frontAvailableProducts={frontAvailableProducts || []}
					backAvailableProducts={backAvailableProducts || []}
					productsCallbacks={{
						onClickInput: handleClickInput,
						onFocusOut: handleFocusOutInput,
						onChangeType: handleTypeChangeInput,
						onActionUpdate: handleUpdateInput,
					}}
					selectionCallbacks={{
						onSelect: handleSelect,
						onSelectOpen: handleOpenSelect,
						onSelectClose: handleCloseSelect,
					}}
				/>

				{Boolean(errorsMessages?.length) && (
					<Grid container direction="column" rowSpacing={0.2} mt={errorsMessages?.length ? 1 : 0.5} alignItems="flex-start">
						{errorsMessages.map((message, key) => (
							<TierAlertError key={`error_tier_product_${message}_${key}`} text={message} />
						))}
					</Grid>
				)}
			</TierRows>

			{innerDeal && (
				<Stack>
					<MiniDealCommissions
						model={innerDeal}
						readOnly={readOnly}
						onActionCapUpdate={(data) => handleUpdateDeal("cap", data)}
						onActionMiniUpdate={(data) => handleUpdateDeal("mini", data)}
					/>
				</Stack>
			)}
		</>
	);
}

TieredProduct.propTypes = {
	tiers: PropTypes.array,
	frontProducts: PropTypes.array,
	backProducts: PropTypes.array,
	miniDeal: PropTypes.shape({
		cap: PropTypes.shape({
			input: PropTypes.string,
			value: PropTypes.number,
		}),
		mini: PropTypes.shape({
			input: PropTypes.string,
			value: PropTypes.number,
		}),
	}),
	loadingProducts: PropTypes.bool,
	additionalErrors: PropTypes.array,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
